import * as React from "react";

import { AVAILABLE_COUNTRIES, AVAILABLE_LANGS, ORIGIN } from "@typings/globals";
import { Contact } from "@typings/footer";

import { getLogoPerOrigin } from "@ui/Assets/Logos";
import { Footer as FooterView } from "@ui/Organisms/Footer";

import { langFromPathname } from "@services/LangFromPathname";

import { usePrismic } from "@hooks/usePrismic";

interface Props {
  variant: ORIGIN;
  country: AVAILABLE_COUNTRIES;
  language?: AVAILABLE_LANGS;
  isStandalone?: boolean;
  isTeaserVersion?: boolean;
  onEmailClick?: () => void;
  onPhoneClick?: () => void;
}

interface State {
  navigationHeader: string;
  officesHeader: string;
  locationsHeader: string;
  appDownloadHeader: string;
  reviewUsHeader: string;
  followUsHeader: string;
  termsOfUseText: string;
  appComingSoonText: string;

  cities: string;
  links: string;
  comingSoonBody: string;
  hashtagLinks: string;
  locationsLeftColumn: string;
  locationsRightColumn: string;
  contact: Contact;

  androidAppUrl: string;
  iosAppUrl: string;
}

const FooterDataWrapper = (props: Props): React.ReactElement => {
  const defaultContact = {
    email: "",
    telephone: "",
    openingHours: "",
    paymentOptions: "",
    contactUs: "",
    contactMedia: "",
  };
  const defaultState = {
    navigationHeader: "",
    officesHeader: "",
    locationsHeader: "",
    appDownloadHeader: "",
    reviewUsHeader: "",
    followUsHeader: "",
    termsOfUseText: "",
    appComingSoonText: "",
    cities: "",
    links: "",
    comingSoonBody: "",
    hashtags: "",
    hashtagLinks: "",
    locationsLeftColumn: "",
    locationsRightColumn: "",
    contact: defaultContact,
    androidAppUrl: "",
    iosAppUrl: "",
  };

  const { getPrismicEntry, isApiReady } = usePrismic();

  const [state, setState] = React.useState<State>(defaultState);

  const setFooterData = async () => {
    const resp = await getPrismicEntry({
      contentType: "footer",
      origin: props.variant,
      lang: props.language || langFromPathname(),
      country: props.country,
    });

    if (!resp?.length) return;

    const { data } = resp[0];

    const mergeParagraphs = (data: { text: string }[]) =>
      data.reduce(
        (acc: { text: string }[], item: { text: "" }) => {
          acc[0].text = acc[0].text + item.text;
          return acc;
        },
        [{ text: "" }],
      );

    const navigationHeader = data.navigation_header[0].text;
    const officesHeader = data.offices_header[0].text;
    const locationsHeader = data.locations_header[0].text;
    const appDownloadHeader = data.app_download_header[0].text;
    const reviewUsHeader = data.review_us_header[0].text;
    const followUsHeader = data.follow_us_header[0].text;
    const termsOfUseText = data.terms_of_use_text[0].text;
    const appComingSoonText = data.app_coming_soon_text[0]?.text;

    const cities = data.we_operate_in[0]?.text;
    const links = data.links[0].text;
    const comingSoonBody = data.coming_soon[0]?.text;
    const hashtagLinks = data.hashtag_links[0].text;
    const locationsLeftColumn = mergeParagraphs(data.locations_left_column)[0]
      .text;
    const locationsRightColumn = mergeParagraphs(data.locations_right_column)[0]
      .text;
    const contact = {
      email: data.email[0].text,
      openingHours: data.opening_hours[0].text,
      paymentOptions: data.payment_options[0].text,
      telephone: data.telephone[0].text,
      contactUs: data.contact_us[0].text,
      contactMedia: data.contact_media[0].text,
    };
    const androidAppUrl = data.android_app_url.url;
    const iosAppUrl = data.ios_app_url.url;

    setState({
      navigationHeader,
      officesHeader,
      locationsHeader,
      appDownloadHeader,
      reviewUsHeader,
      followUsHeader,
      termsOfUseText,
      appComingSoonText,
      cities,
      links,
      comingSoonBody,
      locationsLeftColumn,
      locationsRightColumn,
      hashtagLinks,
      contact,
      androidAppUrl,
      iosAppUrl,
    });
  };

  React.useEffect(() => {
    if (!isApiReady) {
      return;
    }
    setFooterData();
  }, [props.country, props.language, isApiReady]);

  return (
    <FooterView
      variant={props.variant}
      logo={getLogoPerOrigin(props.variant, props.country)}
      cities={state.cities}
      links={state.links}
      comingSoonBody={state.comingSoonBody}
      hashtagLinks={state.hashtagLinks}
      contact={state.contact}
      locationsLeftColumn={state.locationsLeftColumn}
      locationsRightColumn={state.locationsRightColumn}
      navigationHeader={state.navigationHeader}
      officesHeader={state.officesHeader}
      locationsHeader={state.locationsHeader}
      appDownloadHeader={state.appDownloadHeader}
      reviewUsHeader={state.reviewUsHeader}
      followUsHeader={state.followUsHeader}
      termsOfUseText={state.termsOfUseText}
      appComingSoonText={state.appComingSoonText}
      androidAppUrl={state.androidAppUrl}
      iosAppUrl={state.iosAppUrl}
      isStandalone={Boolean(props.isStandalone)}
      language={props.language || langFromPathname()}
      country={props.country}
      isTeaserVersion={props.isTeaserVersion}
      onEmailClick={props.onEmailClick}
      onPhoneClick={props.onPhoneClick}
    />
  );
};

export { FooterDataWrapper };
